import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { playAppAudio } from '../../../utils/audioManager';
import { useNavigate, useLocation } from 'react-router-dom';
import winPointsSound from '../../../assets/audio/winpoints.mp3';
import { useUserStore } from '../../../utils/userStore';
import { RouterConstant } from '../../../routers/index';
import { usePillsStore } from '../../../utils/pillsStore';
import { gamificationStore } from '../../../utils/gamificationStore';
import { UserService } from '../../../services/user/user.service';

export type OnboardingStep = 'GREETING_1' | 'GREETING_2' | 'POINTS' | 'STREAK' | 'STREAK_RESULT' | 'MIC_INSTRUCTIONS' | 'CHAT_INSTRUCTIONS' | 'CHAT_INSTRUCTION_TOOLS' | 'STUDY_MODE' | 'DONE';

const STEPS_ORDER: OnboardingStep[] = [
  'GREETING_1', 'GREETING_2', 'POINTS', 'STREAK', 'STREAK_RESULT', 'MIC_INSTRUCTIONS', 'CHAT_INSTRUCTIONS', 'CHAT_INSTRUCTION_TOOLS', 'STUDY_MODE', 'DONE'
];

export const useOnboarding = (scrollBottomRef: React.RefObject<HTMLDivElement>) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    userInfo, 
    user_attributes, 
    setNavigationReturnPath,
    getGradeLabel,
    getSchoolLabel,
    getUnitsLabel,
    isProfileComplete
  } = useUserStore();

  const labels = {
    grade: getGradeLabel(),
    school: getSchoolLabel(),
    units: getUnitsLabel()
  };

  const isProfileIncomplete = !isProfileComplete();

  const { ROUTERS_PATH } = RouterConstant;

  const isFromProfileUpdate = (location.state as any)?.fromProfileUpdate;
  const resumedStep = (location.state as any)?.onboardingStep as OnboardingStep;
  const initialState = resumedStep || (isFromProfileUpdate ? 'GREETING_2' : 'GREETING_1');

  const pills = usePillsStore();
  const { setPills } = pills;
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(initialState);
  const [showConfirmMessage, setShowConfirmMessage] = useState(isFromProfileUpdate ? true : false);
  const [showMicMessage, setShowMicMessage] = useState(false);
  const [showChatMessage, setShowChatMessage] = useState(false);
  const [showAttachMessage, setShowAttachMessage] = useState(false);
  const [showStudyModeMessage, setShowStudyModeMessage] = useState(false);

  // Initialize chat items based on the starting step
  const [chatItems, setChatItems] = useState<any[]>([]);
  const [isCorrectClicked, setIsCorrectClicked] = useState(false);

  useEffect(() => {
    // Only run this once on mount to populate items if we resumed from a later step
    if (initialState !== 'GREETING_1') {
      const items: any[] = [];

      // Step 1 items (always add if we are past it)
      if (STEPS_ORDER.indexOf(initialState) > STEPS_ORDER.indexOf('GREETING_1')) {
        items.push({
          type: 'bot',
          id: 'greeting-1',
          text: gt('v2Greeting', {
            name: formatValue(userInfo.first_name),
            interpolation: { escapeValue: false }
          }),
          skipAnimation: true
        });
      }

      // Step 2 items if we are strictly after GREETING_2
      if (STEPS_ORDER.indexOf(initialState) > STEPS_ORDER.indexOf('GREETING_2')) {
        items.push({
          type: 'bot',
          id: 'greeting-2',
          text: isProfileIncomplete ? gt('v2MissingProfileInfo') : gt('v2ConfirmLevel', {
            grade: formatValue(labels.grade),
            school: formatValue(labels.school),
            units: formatValue(labels.units),
            interpolation: { escapeValue: false }
          }),
          skipAnimation: true
        });
        items.push({ type: 'confirmation_buttons' });
      }

      // Step 3 items if we are strictly after POINTS
      if (STEPS_ORDER.indexOf(initialState) > STEPS_ORDER.indexOf('POINTS')) {
        items.push({ type: 'success_banner', points: 20 });
      }

      setChatItems(items);
    }
  }, []); // Run only on mount

  const userInitials = useMemo(() => {
    return (userInfo?.first_name?.[0] || '') + (userInfo?.last_name?.[0] || '');
  }, [userInfo]);

  const [streakSelection, setStreakSelection] = useState<number | null | undefined>(undefined);

  const stepAtOrAfter = (step: OnboardingStep) => {
    return STEPS_ORDER.indexOf(currentStep) >= STEPS_ORDER.indexOf(step);
  };

  useEffect(() => {
    if (currentStep === 'GREETING_1') {
      // Add initial greeting
      setChatItems([{
        type: 'bot',
        id: 'greeting-1',
        text: gt('v2Greeting', {
          name: formatValue(userInfo.first_name),
          interpolation: { escapeValue: false }
        })
      }]);

      const timer = setTimeout(() => {
        setCurrentStep('GREETING_2');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  useEffect(() => {
    if (currentStep !== 'GREETING_2') return;

    const timer = setTimeout(() => {
      setShowConfirmMessage(true);
      setChatItems(prev => [...prev, {
        type: 'bot',
        id: 'greeting-2',
        text: isProfileIncomplete ? gt('v2MissingProfileInfo') : gt('v2ConfirmLevel', {
          grade: formatValue(labels.grade),
          school: formatValue(labels.school),
          units: formatValue(labels.units),
          interpolation: { escapeValue: false }
        }),
        onComplete: () => setIsGreeting2Done(true)
      }]);
    }, 1000);

    return () => clearTimeout(timer);
  }, [currentStep]);

  // Handle showing buttons after greeting 2 is done
  const [isGreeting2Done, setIsGreeting2Done] = useState(false);
  useEffect(() => {
    if (isGreeting2Done && currentStep === 'GREETING_2') {
      setChatItems(prev => [...prev, { type: 'confirmation_buttons' }]);
    }
  }, [isGreeting2Done, currentStep]);

  useEffect(() => {
    if (currentStep === 'POINTS') {
      setChatItems(prev => [...prev, { type: 'success_banner', points: 20 }]);
      const timer = setTimeout(() => {
        setCurrentStep('STREAK');
      }, 1000); // Reduced delay from 3s to 1s
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  useEffect(() => {
    if (currentStep === 'STREAK') {
      // First show typing indicator
      setChatItems(prev => [...prev, { type: 'typing', id: 'streak-typing' }]);

      const timer = setTimeout(() => {
        // Remove typing indicator and show streak popup
        setChatItems(prev => {
          const filtered = prev.filter(item => item.id !== 'streak-typing');
          return [...filtered, { type: 'streak_popup' }];
        });
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  useEffect(() => {
    if (currentStep === 'STREAK_RESULT') {
      setChatItems(prev => [...prev, { type: 'streak_result' }]);
    }
  }, [currentStep]);

  const [isMicDone, setIsMicDone] = useState(false);
  const [isChatDone, setIsChatDone] = useState(false);
  const [isAttachDone, setIsAttachDone] = useState(false);
  const [isStudyModeDone, setIsStudyModeDone] = useState(false);

  useEffect(() => {
    if (currentStep === 'MIC_INSTRUCTIONS') {
      const timer = setTimeout(() => {
        setShowMicMessage(true);
        setChatItems(prev => [...prev, {
          type: 'bot',
          id: 'mic-instr',
          text: gt('v2MicInstructions'),
          onComplete: () => setIsMicDone(true)
        }]);

        // After 3 seconds of the message being visible, move to chat instructions
        setTimeout(() => {
          setCurrentStep('CHAT_INSTRUCTIONS');
        }, 3000);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  useEffect(() => {
    if (currentStep === 'CHAT_INSTRUCTIONS') {
      const timer = setTimeout(() => {
        setShowChatMessage(true);
        setChatItems(prev => [...prev, {
          type: 'bot',
          id: 'chat-instr',
          text: gt('v2ChatInstructions'),
          onComplete: () => setIsChatDone(true)
        }]);

        // After 3 seconds, move to attach button instructions
        setTimeout(() => {
          setCurrentStep('CHAT_INSTRUCTION_TOOLS');
        }, 3000);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  useEffect(() => {
    if (currentStep === 'CHAT_INSTRUCTION_TOOLS') {
      const timer = setTimeout(() => {
        setShowAttachMessage(true);
        setChatItems(prev => [...prev, {
          type: 'bot',
          id: 'attach-instr',
          text: gt('v2TakePhoto'),
          onComplete: () => setIsAttachDone(true)
        }]);

        // After 3 seconds move to study mode instructions
        setTimeout(() => {
          setCurrentStep('STUDY_MODE');
        }, 3000);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  useEffect(() => {
    if (currentStep === 'STUDY_MODE') {
      const timer = setTimeout(() => {
        setShowStudyModeMessage(true);
        setChatItems(prev => [...prev, {
          type: 'bot',
          id: 'study-instr',
          text: gt('v2StudyInstructions'),
          onComplete: () => setIsStudyModeDone(true)
        }]);

        // Mark as onboarded in the background when user reaches this step
        const userService = new UserService();
        userService.updateUserAttribute('IsOnboarded', 'true')
          .then(() => {
            useUserStore.getState().updateAttribute('IsOnboarded', 'true');
          })
          .catch(err => {
            console.error('Failed to save IsOnboarded attribute:', err);
          });

        // After 4 seconds move to DONE
        setTimeout(() => {
          setCurrentStep('DONE');
        }, 4000);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  useEffect(() => {
    if (isStudyModeDone && currentStep === 'STUDY_MODE') {
      setChatItems(prev => [...prev, { type: 'study_selector' }]);
    }
  }, [isStudyModeDone, currentStep]);
  useEffect(() => {
    if (currentStep === 'STREAK_RESULT' && streakSelection != null) {
      playAppAudio(winPointsSound);
    }
  }, [currentStep, streakSelection, user_attributes?.isEnableAudio]);

  // Removing manual scroll useEffect to avoid double-jumps.
  // Scrolling is now handled by a centralized height observer in V2Onboarding.


  const handleConfirmGrade = () => {
    setIsCorrectClicked(true);
    setCurrentStep('POINTS');
    gamificationStore.getState().setAward('onboarding_profile_confirm');
  };

  const handleUpdateGrade = () => {
    setNavigationReturnPath(location.pathname + (location.search || ''));
    navigate(ROUTERS_PATH.USERPROFILE, {
      state: {
        fromV2Onboarding: true,
        onboardingStep: currentStep
      }
    });
  };

  const handleSelectStreak = (days: number | null) => {
    setStreakSelection(days);
    gamificationStore.getState().setAward('onboarding_streak_selected', { target_streak_days: days });
    if (days !== null) {
      setCurrentStep('STREAK_RESULT');
      setTimeout(() => {
        setCurrentStep('MIC_INSTRUCTIONS');
      }, 500);
    } else {
      setCurrentStep('MIC_INSTRUCTIONS');
    }
  };

  const handleSelectMode = (mode: string) => {
    switch (mode) {
      case 'study':
        navigate(ROUTERS_PATH.SEARCH_LESSON);
        break;
      case 'exercise':
        navigate(ROUTERS_PATH.SEARCHEXRCISE);
        break;
      case 'homework':
        navigate(ROUTERS_PATH.EXERCISES_HOMEWORK);
        break;
      case 'exam':
        navigate(ROUTERS_PATH.SELFTESTSTART);
        break;
      default:
        break;
    }
  };

  const handleSendMessage = (text: string) => {
    if (!text.trim()) return;

    // 1. Add user message
    setChatItems(prev => [...prev, { type: 'user', text }]);

    // 2. Add static bot response after a short delay
    setTimeout(() => {
      setChatItems(prev => [...prev, {
        type: 'bot',
        text: t('v2OnboardingChatIntro')
      }]);
    }, 1000);
  };

  // Helper to wrap text in bold or return fallback
  const formatValue = (val: string | null | undefined) => {
    if (!val || val.trim() === '') return t('missingInfo');
    return `<b>${val}</b>`;
  };

  const gender = user_attributes.gender ? Number(user_attributes.gender) : null;

  const gt = (key: string, options?: any) => {
    const genderSuffix = gender === 1 ? '_male' : (gender === 2 ? '_female' : '');
    const genderedKey = `${key}${genderSuffix}`;
    return i18n.exists(genderedKey) ? t(genderedKey, options) : t(key, options);
  };

  return {
    t,
    userInfo,
    labels,
    pills,
    currentStep,
    gt,
    showConfirmMessage,
    showMicMessage,
    showChatMessage,
    showAttachMessage,
    showStudyModeMessage,
    streakSelection,
    formatValue,
    stepAtOrAfter,
    handleConfirmGrade,
    handleUpdateGrade,
    handleSelectStreak,
    handleSelectMode,
    handleSendMessage,
    chatItems,
    userInitials,
    isCorrectClicked,
    isChatDisabled: !stepAtOrAfter('STUDY_MODE'),
    isFromProfileUpdate,
    isProfileIncomplete
  };
};
